import type {
  OperationListQuery,
  OperationDetail,
  OperationInput,
  OperationListItem,
  OperationOrderRef,
  OperationPreview,
  OperationPreviewBody,
  Paginated,
  RenewalDiscountActivateBody,
  RenewalDiscountApplicationDetail,
  RenewalDiscountApplicationListItem,
  RenewalDiscountApplicationListQuery,
  RenewalDiscountCustomerPreview,
  RenewalDiscountOperationInput,
  RenewalDiscountRun,
} from '@flux/shared';
import { apiFetch } from '@/lib/api/client.js';

export function fetchOperationsList(query: OperationListQuery): Promise<Paginated<OperationListItem>> {
  return apiFetch<Paginated<OperationListItem>>('/api/operations', {
    query: {
      page: query.page,
      pageSize: query.pageSize,
      sortDir: query.sortDir,
      ...(query.sortBy ? { sortBy: query.sortBy } : {}),
      ...(query.archived !== undefined ? { archived: query.archived } : {}),
      ...(query.lifecycle !== undefined ? { lifecycle: query.lifecycle } : {}),
      ...(query.renewalState !== undefined ? { renewalState: query.renewalState } : {}),
      ...(query.kind !== undefined ? { kind: query.kind } : {}),
      ...(query.search ? { search: query.search } : {}),
    },
  });
}

export function fetchOperationDetail(id: string): Promise<OperationDetail> {
  return apiFetch<OperationDetail>(`/api/operations/${id}`);
}

export function createOperation(body: OperationInput | RenewalDiscountOperationInput): Promise<OperationDetail> {
  return apiFetch<OperationDetail>('/api/operations', { method: 'POST', body });
}

export function updateOperation(id: string, body: OperationInput | RenewalDiscountOperationInput): Promise<OperationDetail> {
  return apiFetch<OperationDetail>(`/api/operations/${id}`, { method: 'PUT', body });
}

export function archiveOperation(id: string): Promise<OperationDetail> {
  return apiFetch<OperationDetail>(`/api/operations/${id}/archive`, { method: 'POST' });
}

export function previewOperation(body: OperationPreviewBody): Promise<OperationPreview> {
  return apiFetch<OperationPreview>('/api/operations/preview', { method: 'POST', body });
}

export function fetchOperationOrders(
  id: string,
  query: { page: number; pageSize: number },
): Promise<Paginated<OperationOrderRef>> {
  return apiFetch<Paginated<OperationOrderRef>>(`/api/operations/${id}/orders`, { query });
}

/** Remise sur le prochain renouvellement (`07` §6) : actions qui appellent Recharge. */
function renewalUrl(id: string, path: string): string {
  return `/api/operations/${id}/renewal-discount/${path}`;
}

export function startRenewalPreview(id: string): Promise<RenewalDiscountRun> {
  return apiFetch<RenewalDiscountRun>(renewalUrl(id, 'preview'), { method: 'POST' });
}

export function fetchRenewalRun(id: string, runId: string): Promise<RenewalDiscountRun> {
  return apiFetch<RenewalDiscountRun>(renewalUrl(id, `runs/${runId}`));
}

export function previewRenewalCustomer(id: string, query: string): Promise<RenewalDiscountCustomerPreview> {
  return apiFetch<RenewalDiscountCustomerPreview>(renewalUrl(id, 'preview-customer'), { method: 'POST', body: { query } });
}

export function activateRenewal(id: string, body: RenewalDiscountActivateBody): Promise<RenewalDiscountRun> {
  return apiFetch<RenewalDiscountRun>(renewalUrl(id, 'activate'), { method: 'POST', body });
}

export function rerunRenewalInitial(id: string): Promise<RenewalDiscountRun> {
  return apiFetch<RenewalDiscountRun>(renewalUrl(id, 'initial'), { method: 'POST' });
}

export function removeRenewalDiscounts(id: string): Promise<RenewalDiscountRun> {
  return apiFetch<RenewalDiscountRun>(renewalUrl(id, 'remove'), { method: 'POST' });
}

export function deactivateRenewal(id: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(renewalUrl(id, 'deactivate'), { method: 'POST' });
}

export function fetchRenewalApplications(
  id: string,
  query: RenewalDiscountApplicationListQuery,
): Promise<Paginated<RenewalDiscountApplicationListItem>> {
  return apiFetch<Paginated<RenewalDiscountApplicationListItem>>(renewalUrl(id, 'applications'), {
    query: {
      page: query.page,
      pageSize: query.pageSize,
      ...(query.status !== undefined ? { status: query.status } : {}),
      ...(query.search !== undefined ? { search: query.search } : {}),
    },
  });
}

export function fetchRenewalApplication(id: string, applicationId: string): Promise<RenewalDiscountApplicationDetail> {
  return apiFetch<RenewalDiscountApplicationDetail>(renewalUrl(id, `applications/${applicationId}`));
}
